import React, { useEffect, useState } from 'react';
import NoDataFields from '../NoDataFields';
import RuleSummary from '../RuleSummary';
import { noDataCondition } from '../../utils/noDataCondition';
import PropTypes from 'prop-types';
import { useForm, useStore } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import {
  Shield,
  Check,
  Hash,
  AlertTriangle,
  Zap,
  TrendingUp,
  Clock,
} from 'lucide-react';

import { extractFieldErrors } from '@/shared/utils/errorUtils';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { ruleSchema } from '@/feature/Rules/schemas/ruleSchemas';
import useDevice from '@/feature/Rules/hooks/useDevice';
import { withSystemMetrics } from '@/feature/Rules/utils/systemMetrics';
import {
  severityOptions,
  ruleTypeOptions,
  operatorOptions,
  statusOptions,
} from '@/feature/Rules/constants/ruleConstants';

import BaseModal from '@/shared/components/modals/core/BaseModal';
import Input from '@/shared/components/elements/Input';
import Select from '@/shared/components/elements/Select';
import Button from '@/shared/components/elements/Button';

// --- Utils ---
const extractRangeValues = (val) => {
  if (Array.isArray(val)) return { v1: val[0], v2: val[1] };
  if (val !== null && typeof val === 'object' && 'min' in val)
    return { v1: val.min, v2: val.max };
  return { v1: val ?? '', v2: '' };
};

// --- Component ---
export default function RuleModal({
  isOpen,
  onClose,
  onSubmit,
  rule = null,
  isLoading = false,
}) {
  const { isMobile } = useResponsive();
  const [serverErrors, setServerErrors] = useState({});
  const isEditMode = !!rule;

  const { v1: defaultVal1, v2: defaultVal2 } = extractRangeValues(
    rule?.condition_expression?.value,
  );

  const form = useForm({
    defaultValues: {
      name: rule?.name || '',
      rule_type: rule?.rule_type || '',
      severity: rule?.severity || '',
      target_type: rule?.target_type || '',
      target_eui: rule?.target_eui || '',
      metric_name: rule?.condition_expression?.metric_name || '',
      operator: rule?.condition_expression?.operator || '',
      value: defaultVal1,
      value2: defaultVal2,
      duration_seconds: rule?.condition_expression?.duration_seconds ?? '3600',
      expected_interval_seconds:
        rule?.condition_expression?.expected_interval_seconds ??
        rule?.condition_expression?.duration_seconds ??
        1800,
      missed_intervals:
        rule?.condition_expression?.missed_intervals ?? (rule ? 1 : 2),
      is_active: rule?.is_active ?? true,
    },
    validatorAdapter: zodValidator(),
    onSubmit: async ({ value }) => {
      try {
        setServerErrors({});
        let condition_expression = {};

        if (value.rule_type === 'lifecycle') {
          condition_expression = noDataCondition(value);
        } else {
          const isRangeOperator =
            value.operator === 'between' || value.operator === 'not_between';

          condition_expression = {
            metric_name: value.metric_name,
            operator: value.operator,
            value: isRangeOperator
              ? {
                  min: parseFloat(value.value),
                  max: parseFloat(value.value2),
                }
              : parseFloat(value.value),
          };

          if (value.rule_type === 'timewindow' && value.duration_seconds) {
            condition_expression.duration_seconds = parseInt(
              value.duration_seconds,
              10,
            );
          }
        }

        const submitData = {
          name: value.name,
          rule_type: value.rule_type,
          severity: value.severity,
          condition_expression,
          is_active: value.is_active,
        };

        if (isEditMode) submitData.ruleId = rule.id;

        await onSubmit(submitData);
        onClose();
      } catch (error) {
        setServerErrors(extractFieldErrors(error));
      }
    },
  });

  const targetType = useStore(form.store, (state) => state.values.target_type);
  const targetEui = useStore(form.store, (state) => state.values.target_eui);

  const isDeviceOrGateway = targetType === 'device' || targetType === 'gateway';
  const addSupportedSystemMetrics = (options) =>
    targetType === 'device' ? withSystemMetrics(options) : options;

  const {
    parameterData,
    isLoadingParameter,
    allParametersList,
    isLoadingAllParameters,
  } = useDevice(1, 100, isDeviceOrGateway ? targetEui : null, {
    enabled: isOpen,
  });

  useEffect(() => {
    if (isOpen) {
      if (rule) {
        const { v1, v2 } = extractRangeValues(rule.condition_expression?.value);
        form.setFieldValue('name', rule.name || '');
        form.setFieldValue('rule_type', rule.rule_type || '');
        form.setFieldValue('severity', rule.severity || '');
        form.setFieldValue('target_type', rule.target_type || '');
        form.setFieldValue('target_eui', rule.target_eui || '');
        form.setFieldValue(
          'metric_name',
          rule.condition_expression?.metric_name || '',
        );
        form.setFieldValue(
          'operator',
          rule.condition_expression?.operator || '',
        );
        form.setFieldValue('value', v1);
        form.setFieldValue('value2', v2);
        form.setFieldValue(
          'duration_seconds',
          rule.condition_expression?.duration_seconds ?? '',
        );
        form.setFieldValue(
          'expected_interval_seconds',
          rule.condition_expression?.expected_interval_seconds ??
            rule.condition_expression?.duration_seconds ??
            1800,
        );
        form.setFieldValue(
          'missed_intervals',
          rule.condition_expression?.missed_intervals ?? 1,
        );
        form.setFieldValue('is_active', rule.is_active ?? true);
      } else {
        form.reset();
        setServerErrors({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, rule]);

  const getMetricOptions = () => {
    // برای دستگاه و گیت‌وی
    if (isDeviceOrGateway && targetEui) {
      if (isLoadingParameter) {
        return [{ value: '', label: 'در حال دریافت متریک‌ها...' }];
      }
      if (parameterData && parameterData.length > 0) {
        return addSupportedSystemMetrics([
          { value: '', label: 'انتخاب متریک' },
          ...parameterData.map((param) => ({
            value: param.name,
            label: param.unit ? `${param.name} (${param.unit})` : param.name,
          })),
        ]);
      }
      return addSupportedSystemMetrics([{ value: '', label: 'انتخاب متریک' }]);
    }

    if (targetType === 'tag') {
      if (isLoadingAllParameters) {
        return [{ value: '', label: 'در حال دریافت متریک‌ها...' }];
      }
      if (allParametersList && allParametersList.length > 0) {
        return withSystemMetrics([
          { value: '', label: 'انتخاب متریک' },
          ...allParametersList.map((param) => ({
            value: param.name,
            label: param.unit ? `${param.name} (${param.unit})` : param.name,
          })),
        ]);
      }
      return withSystemMetrics([{ value: '', label: 'انتخاب متریک' }]);
    }

    return [{ value: '', label: 'لطفا ابتدا هدف را مشخص کنید' }];
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'ویرایش قانون' : 'افزودن قانون جدید'}
      icon={<Shield size={20} />}
      iconVariant="primary"
      size={isMobile ? 'full' : 'lg'}
      closeOnBackdropClick={!isLoading}
      showCloseButton={!isLoading}
      footer={
        <>
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onClose}
            disabled={isLoading}
            className="rounded-xl"
          >
            انصراف
          </Button>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            icon={<Check size={18} />}
            iconPosition="left"
            className="rounded-xl"
            onClick={() => form.handleSubmit()}
          >
            {isEditMode ? 'ذخیره تغییرات' : 'افزودن قانون'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className={isMobile ? 'space-y-4 p-4' : 'space-y-5 p-6'}
      >
        <form.Field
          name="name"
          validators={{
            onChange: ruleSchema.shape.name,
          }}
        >
          {(field) => (
            <Input
              label="نام قانون"
              placeholder="مثال: هشدار دمای بالا"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              error={serverErrors.name || field.state.meta.errors?.[0]?.message}
              icon={<Hash size={18} />}
              disabled={isLoading}
              required
            />
          )}
        </form.Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
          <form.Field
            name="rule_type"
            validators={{
              onChange: ruleSchema.shape.rule_type,
            }}
          >
            {(field) => (
              <Select
                label="نوع قانون"
                placeholder="نوع قانون را انتخاب کنید"
                value={field.state.value}
                onChange={(val) => field.handleChange(val)}
                options={ruleTypeOptions}
                error={
                  serverErrors.rule_type ||
                  field.state.meta.errors?.[0]?.message
                }
                icon={<Zap size={18} />}
                disabled={isLoading}
                required
              />
            )}
          </form.Field>

          <form.Field
            name="severity"
            validators={{
              onChange: ruleSchema.shape.severity,
            }}
          >
            {(field) => (
              <Select
                label="سطح اهمیت"
                value={field.state.value}
                placeholder="سطح اهمیت را انتخاب کنید"
                onChange={(val) => field.handleChange(val)}
                options={severityOptions}
                error={
                  serverErrors.severity || field.state.meta.errors?.[0]?.message
                }
                icon={<AlertTriangle size={18} />}
                disabled={isLoading}
                required
              />
            )}
          </form.Field>
        </div>

        <form.Subscribe
          selector={(state) => ({
            rule_type: state.values.rule_type,
            operator: state.values.operator,
          })}
        >
          {({ rule_type, operator }) => {
            const isLifecycle = rule_type === 'lifecycle';
            const isTimeWindow = rule_type === 'timewindow';
            const isRangeOperator =
              operator === 'between' || operator === 'not_between';

            if (!rule_type) return null;
            return (
              <div
                className={`bg-secondary-6 border-secondary-20 rounded-xl border transition-all duration-300 dark:border-gray-700/50 dark:bg-gray-800/40 ${
                  isMobile ? 'space-y-4 p-3.5' : 'space-y-4.5 p-5'
                }`}
              >
                <div className="mb-2 flex items-center gap-2">
                  <TrendingUp
                    size={18}
                    className="text-secondary-100 transition-colors dark:text-blue-400"
                  />
                  <h3 className="text-tertiary-100 text-sm font-bold transition-colors dark:text-gray-200">
                    شرایط قانون
                  </h3>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-tertiary-80 text-sm font-semibold transition-colors dark:text-gray-300">
                    اگر
                  </span>

                  <div className="min-w-[140px] flex-1">
                    <form.Field
                      name="metric_name"
                      validators={{
                        onChange: isLifecycle
                          ? undefined
                          : ruleSchema.shape.metric_name,
                      }}
                    >
                      {(field) => (
                        <Select
                          placeholder={
                            isLifecycle
                              ? 'همه متریک‌ها (اختیاری)'
                              : 'انتخاب متریک'
                          }
                          value={field.state.value}
                          onChange={(val) => field.handleChange(val)}
                          options={
                            isLifecycle
                              ? [
                                  {
                                    value: '',
                                    label: 'هر پیام داده (همه پارامترها)',
                                  },
                                  ...getMetricOptions().filter(
                                    (item) => item.value,
                                  ),
                                ]
                              : getMetricOptions()
                          }
                          error={
                            serverErrors.metric_name ||
                            field.state.meta.errors?.[0]?.message
                          }
                          disabled={
                            isLoading ||
                            (isDeviceOrGateway && isLoadingParameter) ||
                            (targetType === 'tag' && isLoadingAllParameters)
                          }
                          required={!isLifecycle}
                        />
                      )}
                    </form.Field>
                  </div>

                  {!isLifecycle && (
                    <>
                      <div className="min-w-[140px] flex-1">
                        <form.Field
                          name="operator"
                          validators={{
                            onChange: ruleSchema.shape.operator,
                          }}
                        >
                          {(field) => (
                            <Select
                              placeholder="انتخاب عملگر"
                              value={field.state.value}
                              onChange={(val) => field.handleChange(val)}
                              options={operatorOptions}
                              error={
                                serverErrors.operator ||
                                field.state.meta.errors?.[0]?.message
                              }
                              disabled={isLoading}
                              required
                            />
                          )}
                        </form.Field>
                      </div>

                      <div className="w-full sm:w-32">
                        <form.Field
                          name="value"
                          validators={{
                            onChange: ruleSchema.shape.value,
                          }}
                        >
                          {(field) => (
                            <Input
                              type="number"
                              placeholder="مقدار (min)"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              error={
                                serverErrors.value ||
                                field.state.meta.errors?.[0]?.message
                              }
                              disabled={isLoading}
                              required
                              dir="ltr"
                              inputClassName="!text-left"
                            />
                          )}
                        </form.Field>
                      </div>

                      {isRangeOperator && (
                        <>
                          <span className="text-tertiary-80 text-sm font-semibold transition-colors dark:text-gray-300">
                            و
                          </span>
                          <div className="w-full sm:w-32">
                            <form.Field name="value2">
                              {(field) => (
                                <Input
                                  type="number"
                                  placeholder="مقدار (max)"
                                  value={field.state.value}
                                  onChange={(e) =>
                                    field.handleChange(e.target.value)
                                  }
                                  error={serverErrors.value2}
                                  disabled={isLoading}
                                  required
                                  dir="ltr"
                                  inputClassName="!text-left"
                                />
                              )}
                            </form.Field>
                          </div>
                        </>
                      )}
                      <span className="text-tertiary-80 text-sm font-semibold transition-colors dark:text-gray-300">
                        باشد
                      </span>
                    </>
                  )}
                </div>

                {isLifecycle && <NoDataFields form={form} />}
                {isTimeWindow && (
                  <div className="mt-4 border-t border-gray-200/50 pt-2 dark:border-gray-700/50">
                    <form.Field name="duration_seconds">
                      {(field) => (
                        <Input
                          label={
                            isLifecycle
                              ? 'آستانه زمانی قطع اتصال (ثانیه)'
                              : 'بازه زمانی (ثانیه)'
                          }
                          type="number"
                          placeholder="مثال: 300"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          error={serverErrors.duration_seconds}
                          icon={<Clock size={18} />}
                          disabled={isLoading}
                          required
                          dir="ltr"
                          inputClassName="!text-left"
                        />
                      )}
                    </form.Field>
                  </div>
                )}
              </div>
            );
          }}
        </form.Subscribe>

        {isEditMode && (
          <form.Field name="is_active">
            {(field) => (
              <Select
                label="وضعیت فعالی"
                value={field.state.value}
                onChange={(val) =>
                  field.handleChange(val === 'true' || val === true)
                }
                options={statusOptions}
                disabled={isLoading}
              />
            )}
          </form.Field>
        )}
        <form.Subscribe selector={(state) => state.values}>
          {(values) => (
            <RuleSummary
              values={{ ...values, target_tag_id: rule?.target_tag_id }}
              targetName={rule?.target_name}
              channelName={rule?.rule_channels
                ?.filter((link) => link.is_active)
                .map((link) => link.channel?.name)
                .filter(Boolean)
                .join('، ')}
            />
          )}
        </form.Subscribe>
      </form>
    </BaseModal>
  );
}

RuleModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  rule: PropTypes.object,
  isLoading: PropTypes.bool,
};
